import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { chatApi } from '@/api/client';
import type { ChatSendResponse } from '@/types/chat';
import { chatThreadsQueryKey } from '@/hooks/useChatThreads';

export function chatMessagesQueryKey(profileId: string, threadId: string | null) {
  return ['chat-messages', profileId, threadId] as const;
}

export function useChatMessages(profileId: string, threadId: string | null) {
  return useQuery({
    queryKey: chatMessagesQueryKey(profileId, threadId),
    queryFn: async () => {
      if (!threadId) return [];
      const res = await chatApi.listMessages(profileId, threadId, { limit: 100 });
      if (!res.success) throw new Error('Failed to load messages');
      return res.data?.messages ?? [];
    },
    enabled: Boolean(profileId && threadId),
  });
}

export function useSendChatMessage(profileId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ content, threadId }: { content: string; threadId: string }) => {
      const res = await chatApi.sendMessage(profileId, threadId, {
        content,
        timezone: 'Asia/Kolkata',
      });
      if (!res.success || !res.data) {
        const detail = (res as { detail?: { message?: string } }).detail;
        throw new Error(detail?.message ?? 'Failed to send message');
      }
      return res.data as ChatSendResponse;
    },
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: chatMessagesQueryKey(profileId, variables.threadId),
      });
      void qc.invalidateQueries({ queryKey: chatThreadsQueryKey(profileId) });
    },
  });
}
